import classNames from 'classnames';
import type { ChangeEvent, HTMLAttributes, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Icon } from '../Icon';

import styles from './Tag.module.css';

export type TagSize = 'sm' | 'md' | 'lg';

export interface TagProps extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  size?: TagSize;
  children: ReactNode;
  /** Leading element — a status dot, flag, or avatar (any node). */
  iconLeading?: ReactNode;
  /** Trailing count badge (e.g. a number). */
  count?: ReactNode;
  /** Render a trailing dismiss (×) button. */
  onRemove?: () => void;
  /** Accessible name for the dismiss button. */
  removeLabel?: string;
  /** Controlled selection — presence turns the tag into a selectable checkbox. */
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * Neutral bordered chip for filters, selections, and removable entries. Optionally
 * carries a leading checkbox (selectable), a leading icon, and a trailing count or
 * dismiss button.
 *
 * @interim Ported from the reference design; replaceable by the UI Kit without API breakage.
 */
export function Tag({
  size = 'md',
  children,
  iconLeading,
  count,
  onRemove,
  removeLabel,
  checked,
  defaultChecked,
  onCheckedChange,
  disabled = false,
  className,
  ...rest
}: TagProps) {
  const messages = useUiMessages();
  const resolvedRemoveLabel = removeLabel ?? messages.tag?.remove ?? 'Remove';
  const selectable =
    checked !== undefined || defaultChecked !== undefined || onCheckedChange !== undefined;

  const classes = classNames(
    styles.tag,
    styles[size],
    selectable && styles['has-checkbox'],
    count != null && styles['has-count'],
    Boolean(onRemove) && styles['has-close'],
    disabled && styles.disabled,
    className,
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onCheckedChange?.(event.target.checked);
  };

  const content = (
    <span className={styles.content}>
      {selectable ? (
        <>
          <input
            type="checkbox"
            className={styles.input}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={handleChange}
          />
          <span className={styles.box} aria-hidden="true">
            <span className={styles.check}>
              <Icon name="check" />
            </span>
          </span>
        </>
      ) : null}
      {iconLeading ? <span className={styles.icon}>{iconLeading}</span> : null}
      <span className={styles.text}>{children}</span>
      {count != null ? <span className={styles.count}>{count}</span> : null}
    </span>
  );

  const close = onRemove ? (
    <button
      type="button"
      aria-label={resolvedRemoveLabel}
      className={styles.close}
      onClick={onRemove}
      disabled={disabled}
    >
      <Icon name="x-close" />
    </button>
  ) : null;

  if (selectable) {
    return (
      <label className={classes} {...rest}>
        {content}
        {close}
      </label>
    );
  }

  return (
    <span className={classes} {...rest}>
      {content}
      {close}
    </span>
  );
}
